import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Header from './Header';
import FoodSelector from './FoodSelector';
import MacroTargets from './MacroTargets';
import MealResults from './MealResults';
import CustomFoodForm from './CustomFoodForm';
import PaymentModal from './PaymentModal';
import FeedbackModal from './FeedbackModal';
import GetStartedGuide from './GetStartedGuide';
import AnalyticsModal from './AnalyticsModal';
import UserProfileForm from './UserProfileForm';
import UserProfile from './UserProfile';
import OnboardingTour from './OnboardingTour';
import SmartSuggestionModal from './SmartSuggestionModal';
import { getConversionFactor, foodDatabase, getFoodById } from '../data/foodDatabase';
import usageTracker from '../services/usageTracker';
import customFoodService from '../services/customFoodService';
import { useLocation } from 'react-router-dom';
import supabase from '../supabase/supabase';

const { FiTarget, FiCalculator, FiCheckCircle, FiPlusCircle, FiStar, FiZap, FiMessageCircle, FiHelpCircle, FiBarChart, FiUser, FiSliders } = FiIcons;

// --- Helper Functions ---
const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const getInitialRangeForUnit = (unit) => {
  const u = unit.toLowerCase();
  if (u === 'g' || u === 'ml') return { min: 20, max: 500 };
  if (u === 'cup') return { min: 0.5, max: 5 };
  if (u === 'oz') return { min: 1, max: 16 };
  if (u === 'tbsp') return { min: 1, max: 8 };
  if (u === 'tsp') return { min: 1, max: 12 };
  if (u === 'handful') return { min: 1, max: 5 };
  if (u === 'slice' || u === 'egg' || u === 'piece') return { min: 1, max: 5 };
  return { min: 1, max: 5 };
};

const calculateDynamicServingRange = (food, targets, mealStyle = 'balanced') => {
  const { min: baseMin, max: baseMax } = getInitialRangeForUnit(food.servingUnit);
  if (!targets?.calories) return { min: baseMin, max: baseMax, step: 1 };

  const calorieGoal = parseFloat(targets.calories);
  const proteinGoal = parseFloat(targets.protein) || (calorieGoal * 0.3 / 4);
  const carbGoal = parseFloat(targets.carbs) || (calorieGoal * 0.35 / 4);

  let macroMultiplier = 1.0;
  const isHighProteinFood = food.category === 'Protein' || (food.protein / (food.calories / 4 + 0.1) > 0.4);
  const isHighCarbFood = food.category === 'Carbohydrate' || (food.carbs / (food.calories / 4 + 0.1) > 0.5);

  const userProteinPct = (proteinGoal * 4) / calorieGoal;
  const userCarbPct = (carbGoal * 4) / calorieGoal;

  if (userProteinPct > 0.35 && isHighProteinFood) macroMultiplier = 1.5;
  if (userCarbPct < 0.20 && isHighCarbFood) macroMultiplier = 0.5;

  const macroScore = clamp(macroMultiplier, 0.4, 2.0);
  const calorieFactor = Math.sqrt(calorieGoal / 2000);

  const styleMultipliers = {
    'comfort': 1.2,
    'bowls': 1.1,
    'mexican': 1.1,
    'italian': 1.0,
    'asian': 1.0,
    'fresh': 0.85
  };
  const styleFactor = styleMultipliers[mealStyle] || 1.0;

  let adjustedMin = baseMin * calorieFactor * macroScore * styleFactor;
  let adjustedMax = baseMax * calorieFactor * macroScore * styleFactor;

  let hardMax = Infinity;
  const u = food.servingUnit.toLowerCase();
  if (u === 'g' || u === 'ml') hardMax = 1000;
  else if (u === 'oz') hardMax = 32;
  else if (u === 'cup') hardMax = 10;
  else hardMax = baseMax * 5;

  const hardMin = (u === 'g' || u === 'ml') ? 10 : 0.5;

  let finalMin = clamp(adjustedMin, hardMin, hardMax * 0.6);
  let finalMax = clamp(adjustedMax, finalMin * 1.2, hardMax);

  if (u === 'g' || u === 'ml') {
    finalMin = Math.round(finalMin / 5) * 5;
    finalMax = Math.round(finalMax / 5) * 5;
  } else {
    finalMin = Math.round(finalMin * 2) / 2;
    finalMax = Math.round(finalMax * 2) / 2;
  }

  const range = finalMax - finalMin;
  let step = 1;

  if (u === 'g' || u === 'ml') {
    if (range <= 50) step = 1;
    else if (range <= 150) step = 2;
    else if (range <= 300) step = 5;
    else if (range <= 600) step = 5;
    else step = 10;
    step = Math.max(1, Math.round(step));
  } else {
    if (range <= 3) step = 0.25;
    else if (range <= 6) step = 0.5;
    else step = 1;
  }

  return { min: finalMin, max: finalMax, step };
};

// --- Enhanced Feasibility Logic ---
const ensureFeasibility = (foods, targets) => {
  let currentFoods = JSON.parse(JSON.stringify(foods)); // Deep copy
  const MAX_ITERATIONS = 15; // Increased iterations for robustness

  // Targets
  const tCals = parseFloat(targets.calories) || 2000;
  const tP = parseFloat(targets.protein) || 0;
  const tC = parseFloat(targets.carbs) || 0;
  const tF = parseFloat(targets.fat) || 0;

  // Determine enabled macros
  const checkProtein = tP > 0;
  const checkCarbs = tC > 0;
  const checkFat = tF > 0;

  // Safety buffer multiplier (ensure max is strictly higher than target to allow optimizer room)
  const BUFFER = 1.2;

  for (let i = 0; i < MAX_ITERATIONS; i++) {
    let minStats = { cals: 0, p: 0, c: 0, f: 0 };
    let maxStats = { cals: 0, p: 0, c: 0, f: 0 };

    // 1. Calculate Current Bounds
    currentFoods.forEach(f => {
      const conv = getConversionFactor(f, f.selectedServingUnit);
      const minAmountBase = f.desiredMin / conv;
      const maxAmountBase = f.desiredMax / conv;

      const minMult = minAmountBase / f.servingSize;
      const maxMult = maxAmountBase / f.servingSize;

      minStats.cals += f.calories * minMult;
      minStats.p += f.protein * minMult;
      minStats.c += f.carbs * minMult;
      minStats.f += f.fat * minMult;

      maxStats.cals += f.calories * maxMult;
      maxStats.p += f.protein * maxMult;
      maxStats.c += f.carbs * maxMult;
      maxStats.f += f.fat * maxMult;
    });

    let modified = false;

    // --- CHECK 1: SURPLUS (Target < Min Possible) ---
    const checkSurplus = (currentMin, target, macroName) => {
      if (target > 0 && currentMin > target * 1.02) {
        let bestCandidate = null;
        let maxContribution = -1;

        currentFoods.forEach((f, idx) => {
          const conv = getConversionFactor(f, f.selectedServingUnit);
          const amountBase = f.desiredMin / conv;
          const contribution = (f[macroName] || 0) * (amountBase / f.servingSize);
          const u = f.selectedServingUnit.toLowerCase();
          const hardMin = (u === 'g' || u === 'ml') ? 10 : 0.25;

          if (contribution > maxContribution && f.desiredMin > hardMin) {
            maxContribution = contribution;
            bestCandidate = idx;
          }
        });

        if (bestCandidate !== null) {
          const f = currentFoods[bestCandidate];
          const u = f.selectedServingUnit.toLowerCase();
          const hardMin = (u === 'g' || u === 'ml') ? 10 : 0.25;
          // Aggressively cut min
          const newMin = Math.max(hardMin, Math.floor(f.desiredMin * 0.5));
          if (newMin < f.desiredMin) {
            currentFoods[bestCandidate] = { ...f, desiredMin: newMin };
            return true;
          }
        }
      }
      return false;
    };

    if (checkSurplus(minStats.cals, tCals, 'calories')) { modified = true; continue; }
    if (checkProtein && checkSurplus(minStats.p, tP, 'protein')) { modified = true; continue; }
    if (checkCarbs && checkSurplus(minStats.c, tC, 'carbs')) { modified = true; continue; }
    if (checkFat && checkSurplus(minStats.f, tF, 'fat')) { modified = true; continue; }

    // --- CHECK 2: DEFICIT (Target > Max Possible) ---
    const handleDeficit = (currentMax, target, macroName, foodIdToInject) => {
      if (target > 0 && currentMax < target * BUFFER) {
        // Strategy A: Try to expand "High Density" foods first (Efficient Fix)
        let expanded = false;
        currentFoods.forEach((f, idx) => {
          const density = (f[macroName] || 0) / (f.calories + 1); // Avoid div by 0
          if (density > 0.05 || (macroName === 'calories' && f.calories > 50)) {
            const u = f.selectedServingUnit.toLowerCase();
            let hardMax = 1500; // Increased hard max
            if (u === 'oz') hardMax = 48;
            if (u === 'cup') hardMax = 12;

            if (f.desiredMax < hardMax) {
              const boost = 1.6; // Aggressive boost
              const newMax = Math.min(hardMax, Math.ceil(f.desiredMax * boost));
              if (newMax > f.desiredMax) {
                currentFoods[idx] = { ...f, desiredMax: newMax };
                expanded = true;
              }
            }
          }
        });

        if (expanded) return true;

        // Strategy B: If efficient fix failed, expand ANY food that has the macro (Desperate Fix)
        // This covers the case where user removed the "efficient" food but kept others
        let desperateExpanded = false;
        currentFoods.forEach((f, idx) => {
          if ((f[macroName] || 0) > 0) {
            const u = f.selectedServingUnit.toLowerCase();
            let hardMax = 2000; // Very high limit
            if (u === 'oz') hardMax = 64;

            if (f.desiredMax < hardMax) {
              const boost = 2.0; // Double it
              const newMax = Math.min(hardMax, Math.ceil(f.desiredMax * boost));
              if (newMax > f.desiredMax) {
                currentFoods[idx] = { ...f, desiredMax: newMax };
                desperateExpanded = true;
              }
            }
          }
        });
        if (desperateExpanded) return true;

        // Strategy C: Add a food if expansion didn't work or wasn't enough
        // Only add if we aren't already using it
        if (!currentFoods.some(f => f.id === foodIdToInject)) {
          const foodToAdd = getFoodById(foodIdToInject);
          if (foodToAdd) {
            const { min, max, step } = calculateDynamicServingRange(foodToAdd, targets);
            // Calculate needed amount roughly
            const gap = (target * BUFFER) - currentMax;
            const neededAmount = (gap / (foodToAdd[macroName] || 1)) * 100; // roughly g
            // Convert roughly to serving unit if needed (simplified)
            // We rely on dynamic range mostly, but ensure max is high enough
            const finalMax = Math.max(max, neededAmount * 1.5);

            currentFoods.push({
              ...foodToAdd,
              desiredMin: min,
              desiredMax: Math.round(finalMax),
              step,
              selectedServingUnit: foodToAdd.servingUnit
            });
            return true;
          }
        }
        return false;
      }
      return false;
    };

    // Prioritize Protein -> Carbs -> Fat -> Calories
    if (checkProtein && handleDeficit(maxStats.p, tP, 'protein', 1)) { modified = true; continue; } // 1=Chicken
    if (checkCarbs && handleDeficit(maxStats.c, tC, 'carbs', 17)) { modified = true; continue; } // 17=Rice
    if (checkFat && handleDeficit(maxStats.f, tF, 'fat', 27)) { modified = true; continue; } // 27=Olive Oil

    if (maxStats.cals < tCals * BUFFER) {
      let bestIdx = -1;
      let maxCalVal = -1;
      currentFoods.forEach((f, idx) => {
        if (f.calories > maxCalVal) {
          maxCalVal = f.calories;
          bestIdx = idx;
        }
      });
      if (bestIdx !== -1) {
        const f = currentFoods[bestIdx];
        currentFoods[bestIdx] = { ...f, desiredMax: Math.ceil(f.desiredMax * 1.5) };
        modified = true;
        continue;
      }
    }

    if (!modified) break;
  }

  return currentFoods;
};

const MealCalculator = () => {
  const [selectedFoods, setSelectedFoods] = useState([]);
  const [targets, setTargets] = useState({ calories: '', protein: '', carbs: '', fat: '' });
  const [thresholds, setThresholds] = useState({ calories: 10, protein: 0, carbs: 0, fat: 0, advancedMode: false });
  const [mealPlan, setMealPlan] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [showCustomFoodForm, setShowCustomFoodForm] = useState(false);
  const [customFoods, setCustomFoods] = useState([]);
  const [editingFood, setEditingFood] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [userUsage, setUserUsage] = useState(null);
  const [remainingCalculations, setRemainingCalculations] = useState(5);
  const [isLoadingUsage, setIsLoadingUsage] = useState(true);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showGetStartedGuide, setShowGetStartedGuide] = useState(false);
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [user, setUser] = useState(null);
  const [showUserProfileForm, setShowUserProfileForm] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [setupComplete, setSetupComplete] = useState(false);
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [runTour, setRunTour] = useState(false);
  const [tourMealReady, setTourMealReady] = useState(false);

  // Smart Suggestion State
  const [showSuggestionModal, setShowSuggestionModal] = useState(false);
  const [pendingSuggestion, setPendingSuggestion] = useState(null);

  const location = useLocation();

  const optimizeMeal = (foods, targetMacros, thresholds) => {
    const targetCals = parseFloat(targetMacros.calories);
    const targetProtein = parseFloat(targetMacros.protein) || 0;
    const targetCarbs = parseFloat(targetMacros.carbs) || 0;
    const targetFat = parseFloat(targetMacros.fat) || 0;

    const caloriesThreshold = thresholds.calories / 100;
    const proteinThreshold = thresholds.protein / 100;
    const carbsThreshold = thresholds.carbs / 100;
    const fatThreshold = thresholds.fat / 100;

    const maxAttempts = 15000;
    let validCombinations = [];
    let bestScore = Infinity;
    let macroFlag = thresholds.advancedMode;

    let attempts = 0;
    while (validCombinations.length < 1 && attempts < maxAttempts) {
      const solution = foods.map(food => {
        const conversionFactor = getConversionFactor(food, food.selectedServingUnit);
        const minServing = food.desiredMin;
        const maxServing = food.desiredMax;
        const step = food.step || (['g', 'ml'].includes(food.selectedServingUnit) ? 1 : 0.5);

        const range = maxServing - minServing;
        const stepsCount = Math.floor(range / step);
        const randomStep = Math.floor(Math.random() * (stepsCount + 1));
        let serving = minServing + (randomStep * step);
        serving = Math.max(minServing, Math.min(maxServing, serving));

        const roundedServing = Math.round(serving * 100) / 100;
        const amountInBaseUnit = roundedServing / conversionFactor;
        const baseServingMultiplier = amountInBaseUnit / food.servingSize;

        return {
          ...food,
          actualServingAmount: roundedServing,
          actualCalories: food.calories * baseServingMultiplier,
          actualProtein: food.protein * baseServingMultiplier,
          actualCarbs: food.carbs * baseServingMultiplier,
          actualFat: food.fat * baseServingMultiplier,
        };
      });

      const totals = solution.reduce((acc, item) => ({
        calories: acc.calories + item.actualCalories,
        protein: acc.protein + item.actualProtein,
        carbs: acc.carbs + item.actualCarbs,
        fat: acc.fat + item.actualFat,
      }), { calories: 0, protein: 0, carbs: 0, fat: 0 });

      const calorieError = Math.abs(totals.calories - targetCals) / targetCals;
      const withinCalorieThreshold = calorieError <= caloriesThreshold;

      if (withinCalorieThreshold) {
        if (!macroFlag || (
          (targetProtein === 0 || Math.abs(totals.protein - targetProtein) / targetProtein <= proteinThreshold) &&
          (targetCarbs === 0 || Math.abs(totals.carbs - targetCarbs) / targetCarbs <= carbsThreshold) &&
          (targetFat === 0 || Math.abs(totals.fat - targetFat) / targetFat <= fatThreshold)
        )) {
          let score = calorieError;
          if (macroFlag) {
            if (targetProtein > 0) score += Math.abs(totals.protein - targetProtein) / targetProtein;
            if (targetCarbs > 0) score += Math.abs(totals.carbs - targetCarbs) / targetCarbs;
            if (targetFat > 0) score += Math.abs(totals.fat - targetFat) / targetFat;
          }

          if (score < bestScore) {
            bestScore = score;
            validCombinations = [{ foods: solution, totals, score }];
          }
        }
      }
      attempts++;
    }

    if (validCombinations.length > 0) {
      return validCombinations[0];
    }
    return { foods: [], totals: { calories: 0, protein: 0, carbs: 0, fat: 0 }, score: Infinity, failed: true };
  };

  const updateUsageStats = async () => {
    setIsLoadingUsage(true);
    const safetyTimeout = setTimeout(() => {
      setIsLoadingUsage(false);
    }, 5000);

    try {
      const usage = await usageTracker.getCurrentUserUsage();
      setUserUsage(usage);
      const remaining = await usageTracker.getRemainingFreeCalculations(usage);
      setRemainingCalculations(remaining);
    } catch (error) {
      console.error('Error loading usage stats:', error);
    } finally {
      clearTimeout(safetyTimeout);
      setIsLoadingUsage(false);
    }
  };

  useEffect(() => {
    updateUsageStats();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      const currentUser = session?.user;
      setIsCalculating(false);

      if (event === 'SIGNED_IN' && currentUser) {
        await customFoodService.carryOverGuestFoods(currentUser.id);
      }

      if (currentUser) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', currentUser.id)
          .single();
        setUser({ ...currentUser, ...profile });
      } else {
        setUser(null);
      }
      loadCustomFoods();
      await updateUsageStats();
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const checkForPaymentSuccess = async () => {
      if (location.hash.includes('payment-success') || location.search.includes('session_id')) {
        setTimeout(async () => {
          await updateUsageStats();
        }, 1000);
      }
    };
    checkForPaymentSuccess();
  }, [location]);

  const loadCustomFoods = async () => {
    const foods = await customFoodService.getCustomFoods();
    setCustomFoods(foods);
  };

  useEffect(() => {
    const profileData = localStorage.getItem('user_profile');
    if (profileData) {
      const profile = JSON.parse(profileData);
      setUserProfile(profile);
      setSetupComplete(true);
      if (profile && profile.calculatedTargets) {
        setTargets(profile.calculatedTargets);
        if (profile.calculatedTargets.protein && profile.calculatedTargets.carbs && profile.calculatedTargets.fat) {
          setThresholds({ calories: 10, protein: 15, carbs: 15, fat: 15, advancedMode: true });
        }
      }
    } else {
      setShowUserProfileForm(true);
    }
  }, []);

  const startTourIfNotCompleted = (mealReady = false) => {
    const tourCompleted = localStorage.getItem('meal_calculator_tour_completed');
    if (!tourCompleted) {
      setTourMealReady(mealReady);
      setTimeout(() => setRunTour(true), 500);
    }
  };

  const calculateMeal = async (foodsToUse = selectedFoods, targetsToUse = targets, thresholdsToUse = thresholds, isAutoGeneration = false, skipSuggestions = false) => {
    if (foodsToUse.length === 0 || !targetsToUse.calories) return;

    const needsPayment = await usageTracker.needsPayment();
    if (needsPayment) {
      setShowPaymentModal(true);
      return;
    }

    setIsCalculating(true);

    const suggestionsEnabled = user ? (user.enable_smart_suggestions !== false) : true;

    // --- SMART SUGGESTION SYSTEM ---
    if (!isAutoGeneration && !skipSuggestions && suggestionsEnabled) {
      let feasibleFoods = JSON.parse(JSON.stringify(foodsToUse));
      let verifiedSuccess = false;

      // PASS 1 & 2: Smart & Aggressive Feasibility
      for (let i = 0; i < 3; i++) {
        feasibleFoods = ensureFeasibility(feasibleFoods, targetsToUse);
        const dryResult = optimizeMeal(feasibleFoods, targetsToUse, thresholdsToUse);
        if (!dryResult.failed) {
          verifiedSuccess = true;
          break;
        } else {
          // If standard fix failed, boost ranges for next loop
          feasibleFoods.forEach(f => {
            f.desiredMax = Math.ceil(f.desiredMax * 1.2);
          });
        }
      }

      // PASS 3: Nuclear Option (Global Range Boost)
      // If smart logic failed, force a "Panic Mode" where we double all ranges to find ANY solution
      if (!verifiedSuccess) {
        let panicFoods = JSON.parse(JSON.stringify(foodsToUse));
        panicFoods.forEach(f => {
          const u = f.selectedServingUnit.toLowerCase();
          let hardMax = 2000;
          if (u === 'oz') hardMax = 64;
          if (u === 'cup') hardMax = 12;
          // Massive boost
          f.desiredMax = Math.min(hardMax, Math.ceil(f.desiredMax * 2.0));
        });
        const panicResult = optimizeMeal(panicFoods, targetsToUse, thresholdsToUse);
        if (!panicResult.failed) {
          feasibleFoods = panicFoods;
          verifiedSuccess = true;
        }
      }

      if (verifiedSuccess) {
        const addedFoods = feasibleFoods.filter(f => !foodsToUse.some(existing => existing.id === f.id));

        // Identify Range Adjustments
        const rangeAdjustments = [];
        feasibleFoods.forEach(f => {
          const original = foodsToUse.find(o => o.id === f.id);
          // Check if max increased > 10% or min changed
          if (original && (f.desiredMax > original.desiredMax * 1.1 || f.desiredMin !== original.desiredMin)) {
            rangeAdjustments.push({
              food: original,
              oldMax: original.desiredMax,
              newMax: f.desiredMax,
              unit: f.selectedServingUnit
            });
          }
        });

        // Prioritize showing Additions, then Ranges
        if (addedFoods.length > 0) {
          setIsCalculating(false);
          setPendingSuggestion({
            type: 'add',
            allFoods: feasibleFoods,
            addedFoods
          });
          setShowSuggestionModal(true);
          return;
        }

        if (rangeAdjustments.length > 0) {
          setIsCalculating(false);
          setPendingSuggestion({
            type: 'range',
            adjustments: rangeAdjustments,
            allFoods: feasibleFoods
          });
          setShowSuggestionModal(true);
          return;
        }
      }
      // If even Nuclear Option failed, we proceed to standard calculation to show the failure UI
    }

    setTimeout(async () => {
      let currentFoods = [...foodsToUse];
      let result = { failed: true };
      let maxAttempts = 12;
      let attempt = 0;

      while (attempt < maxAttempts) {
        // Only run ensureFeasibility on retries IF:
        // 1. It's auto-generation (profile setup) - we always want to succeed here
        // 2. OR the food list is very small (helper logic for empty/single food cases)
        // 3. OR we failed at least once AND suggestions are enabled (basic retry)
        const shouldRetryWithFeasibility = isAutoGeneration || (attempt > 0 && !skipSuggestions && suggestionsEnabled) || currentFoods.length < 2;

        if (shouldRetryWithFeasibility) {
          currentFoods = ensureFeasibility(currentFoods, targetsToUse);
        }

        result = optimizeMeal(currentFoods, targetsToUse, thresholdsToUse);
        if (!result.failed) break;
        attempt++;
      }

      if (currentFoods.length !== foodsToUse.length || currentFoods.some((f, i) => f.id !== foodsToUse[i]?.id || f.desiredMax !== foodsToUse[i]?.desiredMax || f.desiredMin !== foodsToUse[i]?.desiredMin)) {
        setSelectedFoods(currentFoods);
      } else if (foodsToUse !== selectedFoods) {
        setSelectedFoods(foodsToUse);
      }

      const isSuccessful = usageTracker.isCalculationSuccessful(result, targetsToUse, thresholdsToUse);
      const updatedUsage = await usageTracker.recordCalculation(isSuccessful);

      if (updatedUsage) {
        setUserUsage(updatedUsage);
        const remaining = await usageTracker.getRemainingFreeCalculations(updatedUsage);
        setRemainingCalculations(remaining);
      }

      setMealPlan(result);
      setIsCalculating(false);
      setCurrentStep(3);
      if (isAutoGeneration) {
        startTourIfNotCompleted(!result.failed);
      }
    }, 100);
  };

  const handleSuggestionAccept = () => {
    if (pendingSuggestion) {
      setShowSuggestionModal(false);
      setSelectedFoods(pendingSuggestion.allFoods);
      // skipSuggestions=true ensures we don't loop
      calculateMeal(pendingSuggestion.allFoods, targets, thresholds, false, true);
      setPendingSuggestion(null);
    }
  };

  const handleSuggestionDecline = () => {
    setShowSuggestionModal(false);
    calculateMeal(selectedFoods, targets, thresholds, false, true);
    setPendingSuggestion(null);
  };

  const handlePaymentSuccess = async () => {
    await updateUsageStats();
    setShowPaymentModal(false);
  };

  const handleUserUpdate = (userData) => {
    setUser(userData);
    updateUsageStats();
  };

  const resetCalculator = () => {
    setSelectedFoods([]);
    setTargets({ calories: '', protein: '', carbs: '', fat: '' });
    setThresholds({ calories: 10, protein: 0, carbs: 0, fat: 0, advancedMode: false });
    setMealPlan(null);
    setCurrentStep(1);
    if (userProfile && userProfile.calculatedTargets) {
      setTargets(userProfile.calculatedTargets);
      if (userProfile.calculatedTargets.protein && userProfile.calculatedTargets.carbs && userProfile.calculatedTargets.fat) {
        setThresholds({ calories: 10, protein: 15, carbs: 15, fat: 15, advancedMode: true });
      }
    }
  };

  const handleSaveCustomFood = async (foodData) => {
    if (editingFood) {
      const updatedFood = await customFoodService.updateCustomFood(editingFood.id, foodData);
      if (updatedFood) {
        setCustomFoods(prev => prev.map(f => f.id === editingFood.id ? updatedFood : f));
      }
    } else {
      const savedFood = await customFoodService.addCustomFood(foodData);
      if (savedFood) {
        setCustomFoods(prev => [savedFood, ...prev]);
      }
    }
    setShowCustomFoodForm(false);
    setEditingFood(null);
  };

  const handleEditCustomFood = (food) => {
    setEditingFood(food);
    setShowCustomFoodForm(true);
  };

  const handleDeleteCustomFood = async (foodId) => {
    if (window.confirm("Are you sure you want to delete this custom food?")) {
      const success = await customFoodService.deleteCustomFood(foodId);
      if (success) {
        setCustomFoods(prev => prev.filter(f => f.id !== foodId));
      }
    }
  };

  const handleCloseCustomFoodForm = () => {
    setShowCustomFoodForm(false);
    setEditingFood(null);
  };

  const canCalculate = selectedFoods.length > 0 && targets.calories;

  const handleMealPlanUpdate = (updatedMealPlan) => {
    setMealPlan(updatedMealPlan);
  };

  const handleAddRecommendedFood = (foodToAdd) => {
    setSelectedFoods(prevFoods => [...prevFoods, foodToAdd]);
    setTimeout(() => {
      // We call calculateMeal without args so it uses the closure's state
      // BUT due to closure staleness in timeouts, we should pass the NEW list explicitly if possible
      // However, we can't easily access the 'result' of the state update here synchronously.
      // So we force a re-render and rely on the button or the user to click calculate,
      // OR we assume the state update will happen before the timeout fires (500ms is usually enough).
      // A safer way is to pass the new list directly:
      calculateMeal([...selectedFoods, foodToAdd]);
    }, 500);
  };

  const handleRemoveFood = (foodId) => {
    if (mealPlan && mealPlan.foods) {
      const updatedFoods = mealPlan.foods.filter(food => food.id !== foodId);
      const newTotals = updatedFoods.reduce((acc, item) => ({
        calories: acc.calories + item.actualCalories,
        protein: acc.protein + item.actualProtein,
        carbs: acc.carbs + item.actualCarbs,
        fat: acc.fat + item.actualFat,
      }), { calories: 0, protein: 0, carbs: 0, fat: 0 });

      setMealPlan({ ...mealPlan, foods: updatedFoods, totals: newTotals });
      setSelectedFoods(prevFoods => prevFoods.filter(food => food.id !== foodId));
    }
  };

  const generateInitialFoods = (preferences, targets) => {
    const styleMap = {
      'comfort': [1, 8, 20, 33, 44],
      'bowls': [1, 4, 13, 25, 34],
      'mexican': [3, 17, 47, 41, 44],
      'italian': [1, 18, 41, 45, 27],
      'asian': [12, 24, 33, 35, 31],
      'fresh': [2, 55, 40, 41, 27]
    };

    let foodIds = [];
    const primaryPref = preferences && preferences.length > 0 ? preferences[0] : 'balanced';

    if (preferences && preferences.length > 0) {
      foodIds = [...styleMap[primaryPref]];
      if (preferences.length > 1) {
        for (let i = 1; i < preferences.length; i++) {
          const pref = preferences[i];
          const extraFoods = styleMap[pref];
          extraFoods.forEach(id => {
            if (!foodIds.includes(id) && foodIds.length < 7) {
              foodIds.push(id);
            }
          });
        }
      }
    } else {
      foodIds = [1, 13, 33, 27, 4];
    }

    const generatedFoods = foodIds.map(id => {
      const food = getFoodById(id);
      if (!food) return null;
      const { min, max, step } = calculateDynamicServingRange(food, targets, primaryPref);
      return {
        ...food,
        desiredMin: min,
        desiredMax: max,
        step: step,
        selectedServingUnit: food.servingUnit
      };
    }).filter(f => f !== null);

    return generatedFoods;
  };

  const handleProfileSave = (profileData) => {
    setUserProfile(profileData);
    setShowUserProfileForm(false);
    setSetupComplete(true);
    localStorage.setItem('user_profile', JSON.stringify(profileData));

    const newTargets = profileData.calculatedTargets;
    let newThresholds = { ...thresholds };

    if (newTargets) {
      setTargets(newTargets);
      if (newTargets.protein && newTargets.carbs && newTargets.fat) {
        newThresholds = { calories: 10, protein: 15, carbs: 15, fat: 15, advancedMode: true };
        setThresholds(newThresholds);
      }
    }

    let willGenerate = false;
    if (profileData.mealPreferences && profileData.mealPreferences.length > 0) {
      let initialFoods = generateInitialFoods(profileData.mealPreferences, newTargets);
      initialFoods = ensureFeasibility(initialFoods, newTargets);
      setSelectedFoods(initialFoods);
      if (initialFoods.length > 0 && newTargets.calories) {
        willGenerate = true;
        calculateMeal(initialFoods, newTargets, newThresholds, true); // isAutoGeneration=true
      }
    }

    if (!willGenerate) {
      startTourIfNotCompleted(false);
    }
  };

  const handleProfileFormClose = () => {
    setShowUserProfileForm(false);
    if (!setupComplete) {
      startTourIfNotCompleted(false);
    }
  };

  const handleLoadFavorite = (favorite) => {
    if (favorite && favorite.meal_plan) {
      const mealToLoad = favorite.meal_plan;
      const foodsToLoad = mealToLoad.foods.map(food => ({ ...food }));
      setSelectedFoods(foodsToLoad);
      setMealPlan(mealToLoad);
      setCurrentStep(3);
      setTimeout(() => {
        setShowUserProfile(false);
      }, 50);
    }
  };

  const handleEditProfile = () => {
    setShowUserProfile(false);
    setShowUserProfileForm(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <OnboardingTour
        run={runTour}
        setRun={setRunTour}
        mealReady={tourMealReady}
      />

      <Header
        user={user}
        onUserUpdate={handleUserUpdate}
        onShowProfile={() => user ? setShowUserProfile(true) : setShowUserProfileForm(true)}
      />

      <div className="py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <div className="flex items-center justify-center gap-3 mb-4">
              <SafeIcon icon={FiTarget} className="text-4xl text-indigo-600" />
              <h1 className="text-4xl font-bold text-gray-800">CreateMyMeal</h1>
            </div>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Select foods with custom serving ranges, set macro targets with flexible thresholds,
              and get optimized meal plans tailored to your preferences.
            </p>

            <div className="mt-4 flex items-center justify-center gap-4">
              {isLoadingUsage ? (
                <div className="bg-gray-100 text-gray-600 px-4 py-2 rounded-full flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-500 border-t-transparent"></div>
                  <span>Loading usage stats...</span>
                </div>
              ) : userUsage?.has_paid ? (
                <div className="bg-green-100 text-green-800 px-4 py-2 rounded-full flex items-center gap-2">
                  <SafeIcon icon={FiStar} className="text-lg" />
                  <span className="font-semibold">Pro User - Unlimited Access</span>
                </div>
              ) : (
                <div className="bg-orange-100 text-orange-800 px-4 py-2 rounded-full flex items-center gap-2">
                  <SafeIcon icon={FiZap} className="text-lg" />
                  <span className="font-semibold">
                    {remainingCalculations} successful calculation{remainingCalculations !== 1 ? 's' : ''} remaining
                  </span>
                </div>
              )}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
              <button
                id="btn-open-profile"
                onClick={() => setShowUserProfileForm(true)}
                className="flex items-center gap-2 bg-white text-indigo-600 px-4 py-2 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 border border-indigo-200"
              >
                <SafeIcon icon={FiUser} className="text-lg" />
                <span>{userProfile ? 'My Profile' : 'Create Profile'}</span>
              </button>
              <button
                id="btn-open-guide"
                onClick={() => setShowGetStartedGuide(true)}
                className="flex items-center gap-2 bg-white text-indigo-600 px-4 py-2 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 border border-indigo-200"
              >
                <SafeIcon icon={FiHelpCircle} className="text-lg" />
                <span>Get Started Guide</span>
              </button>
              <button
                id="btn-open-analytics"
                onClick={() => setShowAnalyticsModal(true)}
                className="flex items-center gap-2 bg-white text-indigo-600 px-4 py-2 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 border border-indigo-200"
              >
                <SafeIcon icon={FiBarChart} className="text-lg" />
                <span>Analytics</span>
              </button>
              <button
                id="btn-open-feedback"
                onClick={() => setShowFeedbackModal(true)}
                className="flex items-center gap-2 bg-white text-indigo-600 px-4 py-2 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 border border-indigo-200"
              >
                <SafeIcon icon={FiMessageCircle} className="text-lg" />
                <span>Feedback</span>
              </button>
            </div>
          </motion.div>

          {/* Progress Steps */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex justify-center mb-8"
          >
            <div className="flex items-center space-x-4">
              {[1, 2, 3].map((step) => (
                <div key={step} className="flex items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${currentStep >= step ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                    {step}
                  </div>
                  {step < 3 && (
                    <div className={`w-16 h-1 mx-2 ${currentStep > step ? 'bg-indigo-600' : 'bg-gray-200'}`} />
                  )}
                </div>
              ))}
            </div>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold text-gray-700">Food Selection</h3>
                <button
                  id="btn-open-add-custom-food"
                  onClick={() => setShowCustomFoodForm(true)}
                  className="flex items-center gap-1 bg-indigo-600 hover:bg-indigo-700 text-white py-2 px-4 rounded-lg transition-colors"
                >
                  <SafeIcon icon={FiPlusCircle} className="text-lg" />
                  <span>Add Custom Food</span>
                </button>
              </div>

              <FoodSelector
                selectedFoods={selectedFoods}
                setSelectedFoods={setSelectedFoods}
                onStepComplete={() => setCurrentStep(Math.max(currentStep, 2))}
                customFoods={customFoods}
                onEditCustomFood={handleEditCustomFood}
                onDeleteCustomFood={handleDeleteCustomFood}
                user={user}
                targets={targets} // Pass targets to help FoodSelector make better default ranges if needed
              />

              <MacroTargets
                targets={targets}
                setTargets={setTargets}
                thresholds={thresholds}
                setThresholds={setThresholds}
                disabled={false}
              />

              <motion.button
                id="btn-optimize-meal"
                whileHover={{ scale: canCalculate ? 1.02 : 1 }}
                whileTap={{ scale: canCalculate ? 0.98 : 1 }}
                onClick={() => calculateMeal()}
                disabled={!canCalculate || isCalculating}
                className={`w-full py-4 px-6 rounded-xl font-semibold text-lg transition-all duration-300 ${canCalculate ? 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg hover:shadow-xl' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
              >
                <div className="flex items-center justify-center gap-3">
                  {isCalculating ? (
                    <>
                      <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent" />
                      <span>Optimizing...</span>
                    </>
                  ) : (
                    <>
                      <SafeIcon icon={FiSliders} className="text-xl" />
                      <span>Optimize My Meal</span>
                    </>
                  )}
                </div>
              </motion.button>

              {mealPlan && (
                <motion.button
                  id="btn-reset-calculator"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={resetCalculator}
                  className="w-full py-3 px-6 rounded-xl font-semibold bg-gray-200 hover:bg-gray-300 text-gray-700 transition-all duration-300"
                >
                  Start New Calculation
                </motion.button>
              )}
            </div>

            <div id="meal-results-panel">
              <AnimatePresence mode="wait">
                {mealPlan ? (
                  <MealResults
                    key="results"
                    mealPlan={mealPlan}
                    targets={targets}
                    thresholds={thresholds}
                    onMealPlanUpdate={handleMealPlanUpdate}
                    selectedFoods={selectedFoods}
                    onAddFood={handleAddRecommendedFood}
                    onRemoveFood={handleRemoveFood}
                    user={user}
                    onRecalculate={() => calculateMeal()}
                  />
                ) : (
                  <motion.div
                    key="placeholder"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="bg-white rounded-2xl shadow-lg p-8 text-center"
                  >
                    <SafeIcon icon={FiCheckCircle} className="text-6xl text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-600 mb-2">
                      Ready to Calculate
                    </h3>
                    <p className="text-gray-500">
                      Select foods with desired amounts and set your targets to generate your optimized meal plan.
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showCustomFoodForm && (
          <CustomFoodForm
            onClose={handleCloseCustomFoodForm}
            onSave={handleSaveCustomFood}
            initialFoodData={editingFood}
          />
        )}
      </AnimatePresence>

      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onPaymentSuccess={handlePaymentSuccess}
        remainingCalculations={remainingCalculations}
        user={user}
        onUserUpdate={handleUserUpdate}
      />

      <AnimatePresence>
        {showFeedbackModal && <FeedbackModal isOpen={showFeedbackModal} onClose={() => setShowFeedbackModal(false)} />}
      </AnimatePresence>

      <AnimatePresence>
        {showGetStartedGuide && <GetStartedGuide isOpen={showGetStartedGuide} onClose={() => setShowGetStartedGuide(false)} />}
      </AnimatePresence>

      <AnimatePresence>
        {showAnalyticsModal && <AnalyticsModal isOpen={showAnalyticsModal} onClose={() => setShowAnalyticsModal(false)} />}
      </AnimatePresence>

      <AnimatePresence>
        {showUserProfileForm && (
          <UserProfileForm
            isOpen={showUserProfileForm}
            onClose={handleProfileFormClose}
            onSave={handleProfileSave}
            initialProfile={userProfile}
            allowSkip={true}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showUserProfile && user && (
          <UserProfile
            user={user}
            onUserUpdate={handleUserUpdate}
            onClose={() => setShowUserProfile(false)}
            onLoadFavorite={handleLoadFavorite}
            onEditProfile={handleEditProfile}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showSuggestionModal && pendingSuggestion && (
          <SmartSuggestionModal
            isOpen={showSuggestionModal}
            onClose={handleSuggestionDecline}
            onAccept={handleSuggestionAccept}
            onDecline={handleSuggestionDecline}
            suggestedFoods={pendingSuggestion.addedFoods}
            type={pendingSuggestion.type}
            adjustments={pendingSuggestion.adjustments}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default MealCalculator;